<template>
    <div class="catalog-forecast-market-container">
        <Loader v-if="loading" :loading="loading" :fixed="true"></Loader>

        <div class="row mb-4">
            <div
                class="col-12 d-flex align-items-center catalog-forecast-market-container-periods mb-5"
                v-if="periodHasZones.length && selectedPeriod"
            >
                <div class="col-auto" @click="updateOffset(-1)" v-if="periodHasZones.length > 8" :class="{ 'disable-arrow': offsetControl === 0 }">
                    <span class="control"><i class="fa fa-arrow-left fa-lg"></i></span>
                </div>
                <div v-bind:key="index" class="col" v-for="(pz, index) in periodHasZones" v-if="index >= offsetControl && index < offsetControl + 8">
                    <span
                        :class="(selectedPeriod.id === pz.period.id ? ' active ' : ' ') + (pz.period.isOpened ? ' open ' : ' ')"
                        @click="changePeriod(pz)"
                    >
                        {{ getDate(pz.period.stampCode, "MMM YY", "second") }}
                    </span>
                </div>
                <div
                    class="col-auto"
                    @click="updateOffset(1)"
                    v-if="periodHasZones.length > 8"
                    :class="{ 'disable-arrow': offsetControl === periodHasZones.length - 8 }"
                >
                    <span class="control"><i class="fa fa-arrow-right fa-lg"></i></span>
                </div>
            </div>

            <ClientColorButton
                :inactive-color="true"
                @click="goToDashboard()"
                v-if="isSuperAdmin() || hasAccessByProfile(PROFILES.COORDINATOR, APPLICATION.CATALOG_FORECAST_ID)"
            >
                {{ $t("catalogForecast.market.backToDashboard") }}
            </ClientColorButton>
        </div>

        <div v-if="periodHasZone && selectedPeriod">
            <div class="row catalog-forecast-market-container-header d-flex align-items-end mb-4">
                <div class="col-12 col-xl">
                    <p class="title mb-2">{{ periodHasZone.zone.name.toUpperCase() }}</p>
                    <p class="subtitle mb-5">{{ periodTitle }}</p>
                    <PeriodDates :period="selectedPeriod"></PeriodDates>
                </div>
                <div class="col-xl-2"></div>
                <div class="col d-flex justify-content-end mt-4 mt-xl-0">
                    <div>
                        <div class="mb-5 rechallenged d-flex align-items-center" v-if="periodHasZone.status === PERIOD_STATUS.RECHALLENGED">
                            <span class="mr-3" v-html-safe="$t('catalogForecast.market.rechallenged')"></span>
                            <ClientColorButton
                                size="m"
                                :rounded100="true"
                                background-color="#F9C53C"
                                color="#fff"
                                @click="openModalRechallengeInfos()"
                            >
                                <i class="fa fa-undo-alt fa-lg"></i>
                            </ClientColorButton>
                        </div>
                        <div class="mb-5">
                            <ClientColorButton :inactive-color="true" class="mr-5" @click="checkDownload()">
                                <p class="mb-1">{{ $t("common.download") }}</p>
                                <img class="position-relative" src="../../../../static/img/logo-xls.svg" />
                            </ClientColorButton>
                            <DownloadInfos v-if="paramsModalDownloadInfos" :params="paramsModalDownloadInfos" @yes="download()"></DownloadInfos>

                            <ClientColorButton :inactive-color="true" @click="checkUpload" v-if="canFirmorderUpdate || canForecastUpdate">
                                <p class="mb-1">{{ $t("common.upload") }}</p>
                                <img class="position-relative" src="../../../../static/img/logo-xls.svg" />
                            </ClientColorButton>
                            <input type="file" class="d-none" accept=".xlsx" ref="uploadTemplate" @change="changeUploadFile()" />
                            <UploadInfos v-if="paramsModalUploadInfos" :params="paramsModalUploadInfos" @yes="openUploadFileExplorer()"></UploadInfos>
                        </div>
                        <div class="d-flex justify-content-end">
                            <div v-if="canFirmorderUpdate || canForecastUpdate">
                                <ClientColorButton :rounded="true" background-color="#80D837" class="mr-3" @click="save(PERIOD_STATUS.STARTED)">
                                    {{ $t("common.save") }}
                                </ClientColorButton>
                                <ClientColorButton :rounded="true" background-color="#80D837" class="mr-5" @click="save(PERIOD_STATUS.SUBMITTED)">
                                    {{ $t("catalogForecast.market.saveAndSubmit") }}
                                </ClientColorButton>
                            </div>
                            <History
                                :is-dark="false"
                                :object-type="OBJECT_TYPE.PERIOD_HAS_ZONES"
                                :object="periodHasZone"
                                :name="periodHasZone.zone.name.toUpperCase() + ' - ' + periodTitle"
                            >
                            </History>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row catalog-forecast-market-container-search d-flex align-items-start">
                <div class="col-auto mt-3">
                    <span>{{ $t("catalogForecast.market.searchItem") }}</span>
                    <div>
                        <input type="text" id="searchBySearch" v-model.trim="search" @input="debounceSearch" class="rem3rch-input-text" />
                        <label for="searchBySearch" class="m-0 pointer ml-3 mr-5"><i class="fa fa-search fa-lg color-grey"></i></label>
                    </div>
                </div>
                <div class="col-12 col-xl mt-3 mr-4">
                    <span>{{ $t("catalogForecast.market.searchByCategory") }}</span>
                    <div class="row">
                        <div v-bind:key="index" class="col-auto mb-3" v-for="(categoriesLevel, index) in categoriesLevelSelected">
                            <Multiselect
                                v-if="categoriesLevelSelected[index]"
                                :options="categoriesLevelSelected[index]"
                                v-model="categoriesSelected[index]"
                                label="name"
                                track-by="id"
                                :multiple="false"
                                :select-label="''"
                                :close-on-select="true"
                                :show-labels="true"
                                :placeholder="$t('catalogForecast.market.selectCategory') + ' ' + (index + 1)"
                                :deselect-label="$t('common.delete')"
                                @input="updateCategory($event, index)"
                            ></Multiselect>
                        </div>
                    </div>
                </div>
            </div>

            <div class="catalog-forecast-market-container-table mt-5">
                <div class="row mb-4">
                    <div class="col">
                        <span class="count-reference"
                            >{{ periodHasComponentsPagination.totalItems }}
                            {{ $t("catalogForecast.market.reference" + (periodHasComponentsPagination.totalItems > 1 ? "s" : "")) }}</span
                        >
                    </div>
                    <div class="col">
                        <DefaultPagination
                            :item-pagination="periodHasComponentsPagination"
                            :offset="offset"
                            @change="changeOffset"
                        ></DefaultPagination>
                    </div>
                </div>

                <div class="d-flex">
                    <div class="table-column">
                        <table cellspacing="0" cellpadding="0" ref="table">
                            <thead>
                                <tr>
                                    <th class="sap">SAP</th>
                                    <th class="ean">EAN</th>
                                    <th class="name">{{ $t("catalogForecast.market.nameLabel") }}</th>
                                    <th class="unit">{{ $t("catalogForecast.market.unitLabel") }}</th>
                                    <th class="info">INFO</th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-for="periodHasComponent in periodHasComponents">
                                    <tr v-bind:key="periodHasComponent.id">
                                        <td class="sap">{{ periodHasComponent.component.codeSap }}</td>
                                        <td class="ean">{{ periodHasComponent.component.codeEan }}</td>
                                        <td class="name">
                                            <span
                                                :id="'name' + periodHasComponent.component.cpntId"
                                                :ref="'name' + periodHasComponent.component.cpntId"
                                                v-b-tooltip.hover.bottom="periodHasComponent.component.CPTR_name"
                                                v-b-tooltip.disabled="disableTooltip('name' + periodHasComponent.component.cpntId)"
                                            >
                                                {{ periodHasComponent.component.CPTR_name }}
                                            </span>
                                        </td>
                                        <td class="unit text-center">
                                            {{ periodHasComponent.component.unitPerBox ? periodHasComponent.component.unitPerBox : "-" }}
                                        </td>
                                        <td class="info">
                                            <span class="pointer" @click="openModalComponentInfos(periodHasComponent)"
                                                ><i class="fa fa-info-circle fa-lg"></i
                                            ></span>
                                        </td>
                                    </tr>
                                </template>
                                <tr v-if="!periodHasComponents || !periodHasComponents.length" class="notfound">
                                    <td :colspan="5">{{ $t("catalogForecast.market.noItem") }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="table-column table-responsive">
                        <table cellspacing="0" cellpadding="0" ref="table">
                            <thead>
                                <tr>
                                    <template v-for="column in columnsFiltered" v-if="columnsFiltered.length">
                                        <th class="header" :class="column.firmorder ? 'firmorder' : 'forecast'" v-bind:key="column.id">
                                            <div class="d-flex align-items-start flex-column">
                                                <span>{{ column.firmorder ? "firmorder" : "forecast" }}</span>
                                                <p v-html-safe="getHeaderLabel(column)"></p>
                                            </div>
                                        </th>
                                    </template>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-for="periodHasComponent in periodHasComponents">
                                    <tr v-bind:key="periodHasComponent.id">
                                        <template v-for="column in columnsFiltered" v-if="columnsFiltered.length">
                                            <td
                                                v-bind:key="column.id"
                                                @click="focusInput(`input_${periodHasComponent.id}_${column.id}`)"
                                                :class="
                                                    (!isCatalogComponent(periodHasComponent.component, column) ||
                                                    !isActiveComponent(periodHasComponent.component) ||
                                                    isDolceGabbana2023(column)
                                                        ? 'not-active '
                                                        : ' ') + (column.firmorder ? 'firmorder' : 'forecast')
                                                "
                                            >
                                                <input
                                                    type="text"
                                                    class="form-control"
                                                    :id="`input_${periodHasComponent.id}_${column.id}`"
                                                    :ref="`input_${periodHasComponent.id}_${column.id}`"
                                                    :value="getComponentQuantity(column, periodHasComponent.id)"
                                                    v-on:keydown="tab($event, `input_${periodHasComponent.id}_${column.id}`, columnsFiltered.length)"
                                                    @input="updateFirmorderQuantity($event, column, periodHasComponent)"
                                                    v-digitsonly
                                                    :readonly="
                                                        !canCellUpdate(
                                                            column.firmorder ? canFirmorderUpdate : canForecastUpdate,
                                                            periodHasComponent.component,
                                                            column
                                                        )
                                                    "
                                                />
                                            </td>
                                        </template>
                                    </tr>
                                </template>
                                <tr v-if="!periodHasComponents || !periodHasComponents.length" class="notfound">
                                    <td :colspan="columnsFiltered.length"></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div class="row mt-4">
                    <div class="col">
                        <span class="count-reference"
                            >{{ periodHasComponentsPagination.totalItems }}
                            {{ $t("catalogForecast.market.reference" + (periodHasComponentsPagination.totalItems > 1 ? "s" : "")) }}</span
                        >
                    </div>
                    <div class="col">
                        <DefaultPagination
                            class="mb-4"
                            :item-pagination="periodHasComponentsPagination"
                            :offset="offset"
                            @change="changeOffset"
                        ></DefaultPagination>
                    </div>
                </div>

                <RechallengeInfos
                    v-if="paramsModalRechallengeInfos"
                    :params="paramsModalRechallengeInfos"
                    @close="paramsModalRechallengeInfos = null"
                ></RechallengeInfos>
                <ComponentInfos
                    v-if="paramsModalComponentInfos"
                    :params="paramsModalComponentInfos"
                    @close="paramsModalComponentInfos = null"
                ></ComponentInfos>
            </div>
        </div>
    </div>
</template>
<script>
import { COMPONENT_STATUS, FUNCTIONALITY, PERIOD_STATUS } from "@/components/catalogForcast/constants";
import periodHasZoneService from "@/services/cfPeriodHasZones";
import periodHasComponentsService from "@/services/cfPeriodHasComponents";
import PeriodDates from "@/components/catalogForcast/elements/PeriodDates";
import { moment } from "@/utils/moment";
import Loader from "@/components/loader/loader";
import Multiselect from "vue-multiselect";
import Template from "@/plano/template";
import debounce from "lodash/debounce";
import TryLoad from "@/mixins/TryLoadMixin";
import Pagination from "@/components/shades/pagination";
import DefaultPagination from "@/components/tools/DefaultPagination";
import History from "@/components/history/History";
import { OBJECT_TYPE } from "@/components/history/constants";
import ComponentInfos from "@/components/catalogForcast/elements/ComponentInfos";
import RechallengeInfos from "@/components/catalogForcast/elements/RechallengeInfos";
import DownloadInfos from "@/components/catalogForcast/elements/DownloadInfos";
import Globals from "@/components/globals";
import UploadInfos from "@/components/catalogForcast/elements/UploadInfos";
import { PROFILES } from "@/plugins/AccessUserPlugin";
import { APPLICATION } from "@/components/admin/brand/constant";
import { JustDigits } from "@/utils/JustDigit";
import $ from "jquery";

export default {
    components: {
        UploadInfos,
        DownloadInfos,
        RechallengeInfos,
        ComponentInfos,
        History,
        DefaultPagination,
        Pagination,
        Template,
        Loader,
        PeriodDates,
        Multiselect,
        TryLoad
    },
    props: {
        periodHasZones: {
            type: Array,
            required: true
        },
        periodHasZone: {
            type: Object,
            required: true
        },
        categories: {
            type: Array,
            required: true
        },
        filters: {
            type: Map,
            required: true
        },
        paramAddImagesToArticles: {
            required: true
        },
        isEditing: {
            type: Boolean,
            required: true
        },
        reload: {
            type: Boolean,
            required: true
        }
    },
    data() {
        return {
            PROFILES,
            APPLICATION,
            PERIOD_STATUS,
            COMPONENT_STATUS,
            OBJECT_TYPE,
            FUNCTIONALITY,
            periodHasComponentsPagination: [],
            periodHasComponents: [],
            categoriesLevelSelected: [],
            categoriesSelected: [],
            loading: false,
            search: "",
            quantitiesToUpdate: [],
            paramsModalUpdateQuantity: null,
            paramsModalComponentInfos: null,
            paramsModalRechallengeInfos: null,
            paramsModalDownloadInfos: null,
            paramsModalUploadInfos: null,
            limit: 30,
            offset: 0,
            selectedPeriod: null,
            selectedDate: this.getDate(moment(), "YYYY-MM", "month"),
            offsetControl: 0
        };
    },
    mixins: [TryLoad],
    computed: {
        periodTitle() {
            return (
                "PERIOD " +
                moment(this.selectedPeriod.stampCode, "YYYYMM")
                    .startOf("month")
                    .format("MMM YYYY")
                    .toUpperCase()
            );
        },
        columnsFiltered() {
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            return this.periodHasZone.columns.sort((c1, c2) => {
                return c1.number <= c2.number ? -1 : 1;
            });
        },
        needModalRechallengeInfos() {
            const ids = JSON.parse(localStorage.getItem("rechallengesMessageRead"));
            return (
                this.periodHasZone.lastRechallenge &&
                this.periodHasZone.status === PERIOD_STATUS.RECHALLENGED &&
                (!ids || !ids.some(id => id === this.periodHasZone.lastRechallenge.id))
            );
        },
        quantities() {
            if (!this.periodHasComponents) {
                return [];
            }
            const quantities = [];
            this.periodHasComponents.forEach(pc => {
                if (!pc.quantities) return;
                pc.quantities.forEach(q => {
                    if (!quantities[pc.id]) {
                        quantities[pc.id] = [];
                    }
                    quantities[pc.id][q.column.id] = {
                        periodHasComponent: pc.id,
                        status: pc.component.statusName,
                        availabilityDate: pc.component.availabilityDate,
                        column: q.column.id,
                        stamp: q.column.stamp,
                        id: q.id,
                        quantity: q.quantity
                    };
                });
            });
            return quantities;
        },
        currentStamp() {
            return localStorage.getItem("currentStamp");
        },
        isBetweenOpeningAndFirmorderDates() {
            return (
                moment().unix() >= moment(this.selectedPeriod.openingDate).unix() &&
                moment().format("YYYY-MM-DD") <= moment(this.selectedPeriod.firmorderClosingDate).format("YYYY-MM-DD")
            );
        },
        isBetweenFirmorderAndForecastDates() {
            return (
                moment().unix() >= moment(this.selectedPeriod.firmorderClosingDate).unix() &&
                moment().format("YYYY-MM-DD") <= moment(this.selectedPeriod.forecastClosingDate).format("YYYY-MM-DD")
            );
        },
        canFirmorderUpdate() {
            return (
                this.selectedPeriod.isOpened &&
                (this.isBetweenOpeningAndFirmorderDates ||
                    (this.isBetweenFirmorderAndForecastDates &&
                        (this.hasAccessByProfile(PROFILES.COORDINATOR, APPLICATION.CATALOG_FORECAST_ID) || this.isSuperAdmin())))
            );
        },
        canForecastUpdate() {
            return this.selectedPeriod.isOpened;
        }
    },
    methods: {
        getDate(date, format, start = "second") {
            return moment(date, "YYYYMM")
                .startOf("month")
                .format(format)
                .toUpperCase();
        },
        updateOffset(value) {
            if (this.offsetControl + value >= 0 && this.offsetControl + 8 + value <= this.periodHasZones.length) {
                this.offsetControl = this.offsetControl + value;
            }
        },
        async changePeriod(periodHasZone, reload = true) {
            if (await this.warningSaveQuantities(this.$t("catalogForecast.market.warningSave"))) {
                this.selectedPeriod = periodHasZone.period;
                localStorage.setItem("currentStamp", this.selectedPeriod.stampCode);
                if (reload) {
                    this.$emit("change", periodHasZone);
                }
                this.$emit("updateIsEdit", false);
            }
        },
        openModalRechallengeInfos() {
            let ids = JSON.parse(localStorage.getItem("rechallengesMessageRead"));
            if (!ids) {
                ids = [];
            }
            ids.push(this.periodHasZone.lastRechallenge.id);
            localStorage.setItem("rechallengesMessageRead", JSON.stringify(ids));
            this.paramsModalRechallengeInfos = {
                isActive: true,
                periodHasZone: this.periodHasZone
            };
        },
        async checkDownload() {
            const res = await this.tryLoad(() => periodHasZoneService.checkDownloadXls(this.periodHasZone.id));
            if (res.isDownloaded) {
                this.openModalDownloadInfos(res.downloads);
            } else {
                await this.download();
            }
        },
        openModalDownloadInfos(downloads) {
            this.paramsModalDownloadInfos = {
                isActive: true,
                downloads: downloads,
                periodHasZone: this.periodHasZone
            };
        },
        async download() {
            const resp = await this.tryLoad(() => periodHasZoneService.downloadXls(this.periodHasZone.id));
            const a = document.createElement("a");
            a.href = "data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64," + resp;
            a.setAttribute(
                "download",
                this.periodHasZone.id +
                    "-Period-" +
                    this.periodHasZone.period.stampCode +
                    "-Zone-" +
                    this.periodHasZone.zone.name.replace(".", "") +
                    "_" +
                    Globals.moment(new Date()).format("YYYY-MM-DD_HH-mm-ss") +
                    ".xlsx"
            );
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        },
        async checkUpload() {
            if (await this.warningSaveQuantities(this.$t("catalogForecast.market.warningSaveBeforeUpload"))) {
                await this.tryLoad(() =>
                    periodHasZoneService.checkUploadXls(this.periodHasZone.id).then(res => {
                        if (res.isUploaded) {
                            this.openModalUploadInfos(res.uploads);
                        } else {
                            this.openUploadFileExplorer();
                        }
                    })
                );
            }
        },
        openModalUploadInfos(uploads) {
            this.paramsModalUploadInfos = {
                isActive: true,
                uploads: uploads,
                periodHasZone: this.periodHasZone
            };
        },
        openUploadFileExplorer() {
            this.$refs["uploadTemplate"].click();
            return false;
        },
        changeUploadFile() {
            const input = this.$refs["uploadTemplate"];

            if (input && input.files && input.files[0]) {
                const name = input.files[0].name;
                const id = name.split("-")[0];
                const extension = name.split(".").pop();
                if (parseInt(id, 10) === this.periodHasZone.id && extension === "xlsx") {
                    this.upload(input.files[0]);
                } else {
                    input.value = "";
                    this.dialogError("", "", this.$t("catalogForecast.market.wrongFileUploaded"));
                }
            } else {
                input.value = "";
                this.dialogError("", "", this.$t("catalogForecast.market.wrongFileUploaded"));
            }
        },
        upload(file) {
            this.loading = true;
            periodHasZoneService.uploadXls(this.periodHasZone.id, file).then(async res => {
                const promise = res.json();
                if (res && res.status === 500) {
                    promise.then(message => {
                        this.loading = false;
                        this.$refs["uploadTemplate"].value = "";
                        let trad = "error";
                        if (message === "WRONG_TEMPLATE") {
                            trad = "catalogForecast.market.wrongFileUploaded";
                        } else if (message === "CORRUPTED_FILE") {
                            trad = "catalogForecast.market.corruptedFileUploaded";
                        }
                        this.dialogError("", "", this.$t(trad));
                    });
                } else {
                    this.loading = false;
                    this.$refs["uploadTemplate"].value = "";
                    this.quantitiesToUpdate = [];
                    this.$emit("updateIsEdit", false);
                    this.$emit("reload");
                    await this.loadPeriodHasComponents();
                    this.dialogSuccess(this.$t("catalogForecast.market.uploadSuccess"));
                }
            });
        },
        debounceSearch: debounce(function() {
            this.offset = 0;
            if (this.search) {
                this.filters.set("search", this.search);
                this.loadPeriodHasComponents();
            } else {
                this.filters.delete("search");
                this.loadPeriodHasComponents();
            }
        }, 400),
        updateCategory(category, index) {
            this.offset = 0;
            const tmpLevel = Object.assign([], this.categoriesLevelSelected);
            const tmp = Object.assign([], this.categoriesSelected);
            if (category) {
                if (tmpLevel[index + 1]) {
                    tmp[index + 1] = { name: null };
                    tmpLevel.forEach((cl, i) => {
                        if (index < i) {
                            delete tmpLevel[i];
                            delete tmp[i];
                        }
                    });
                }
                if (category.subCategories && category.subCategories.length) {
                    tmpLevel[index + 1] = category.subCategories;
                }
            } else {
                tmpLevel.forEach((cl, i) => {
                    if (i > index) {
                        delete tmpLevel[i];
                        delete tmp[i];
                    }
                });
            }
            this.categoriesLevelSelected = [];
            this.categoriesLevelSelected = tmpLevel;
            this.categoriesSelected = [];
            this.categoriesSelected = tmp;

            this.filters.set(
                "categories",
                this.categoriesSelected
                    .filter(c => c && c.id)
                    .map(c => c.id)
                    .join(",")
            );
            this.loadPeriodHasComponents();
        },
        async loadPeriodHasComponents() {
            this.filters.set("limit", this.limit);
            this.filters.set("offset", this.offset);
            this.periodHasComponentsPagination = await this.tryLoad(() => periodHasComponentsService.get(this.filters));
            this.periodHasComponents = this.periodHasComponentsPagination.items;
        },
        isActiveComponent(component) {
            return component.statusName === COMPONENT_STATUS.ACTIVE;
        },
        isCatalogComponent(component, column) {
            if (moment(component.availabilityDate, "YYYYMMDD", true).isValid()) {
                return moment(column.stamp, "YYYYMM").format("YYYYMM") >= moment(component.availabilityDate, "YYYYMMDD").format("YYYYMM");
            }
            // else if (!moment(component.availabilityDate, "YYYYMMDD", true).isValid()) {
            //     return false;
            // }
            return true;
        },
        isDolceGabbana2023(column) {
            return column.stamp > "202212" && this.periodHasZone.period.brand.id === 5;
        },
        canCellUpdate(canFirmorderOrForecastUpdate, component, column) {
            return (
                canFirmorderOrForecastUpdate &&
                this.isActiveComponent(component) &&
                this.isCatalogComponent(component, column) &&
                !this.isDolceGabbana2023(column)
            );
        },
        async changeOffset(offset) {
            this.offset = offset;
            await this.loadPeriodHasComponents();
        },
        getHeaderLabel(column) {
            return moment(column.stamp, "YYYYMM")
                .startOf("month")
                .format("MMM YY")
                .toUpperCase();
        },
        getComponentQuantity(column, pcId) {
            let quantity = null;
            if (this.quantitiesToUpdate.some(q => q.column === column.id && q.periodHasComponent === pcId)) {
                quantity = this.quantitiesToUpdate.find(q => q.column === column.id && q.periodHasComponent === pcId).quantity;
            } else {
                quantity = this.quantities[pcId] && this.quantities[pcId][column.id] ? this.quantities[pcId][column.id].quantity : null;
            }
            return quantity;
        },
        focusInput(id) {
            this.$refs[id][0].focus();
        },
        updateFirmorderQuantity(event, column, pc) {
            if (this.quantities[pc.id] && this.quantities[pc.id][column.id]) {
                this.quantities[pc.id][column.id].quantity = event.target.value ? parseInt(event.target.value, 10) : null;
            } else {
                if (!this.quantities[pc.id]) {
                    this.quantities[pc.id] = [];
                }
                this.quantities[pc.id][column.id] = {
                    periodHasComponent: pc.id,
                    status: pc.component.statusName,
                    availabilityDate: pc.component.availabilityDate,
                    column: column.id,
                    stamp: column.stamp,
                    quantity: event.target.value ? parseInt(event.target.value, 10) : null
                };
            }
            const index = this.quantitiesToUpdate.findIndex(q => q && q.periodHasComponent === pc.id && q.column === column.id);
            if (index > -1) {
                delete this.quantitiesToUpdate.splice(index, 1);
            }
            this.quantitiesToUpdate.push(this.quantities[pc.id][column.id]);
            this.$emit("updateIsEdit", true);
        },
        openModalComponentInfos(periodHasComponent) {
            this.paramsModalComponentInfos = {
                isActive: true,
                periodHasComponent: periodHasComponent,
                paramAddImagesToArticles: this.paramAddImagesToArticles
            };
        },
        updateQuantities(quantities, pcId) {
            this.quantities[pcId] = quantities;
        },
        tab(event, input, columnLength) {
            let el = null;
            const inputs = $(this.$refs[input])
                .closest("tbody")
                .find(":input");
            event.stopPropagation();
            switch (event.keyCode) {
                case 37:
                    if (inputs.index($(this.$refs[input])) - 1 >= 0) {
                        el = inputs.eq(inputs.index($(this.$refs[input])) - 1);
                        el.focus();
                    }
                    break;
                case 38:
                    if (inputs.index($(this.$refs[input])) - columnLength >= 0) {
                        el = inputs.eq(inputs.index($(this.$refs[input])) - columnLength);
                        el.focus();
                    }
                    break;
                case 39:
                    el = inputs.eq(inputs.index($(this.$refs[input])) + 1);
                    el.focus();
                    break;
                case 40:
                    el = inputs.eq(inputs.index($(this.$refs[input])) + columnLength);
                    el.focus();
                    break;
            }
        },
        goToDashboard() {
            this.$router.push({ path: "/admin/catalog_forecast/dashboard" });
        },
        save(type = "save") {
            this.loading = true;
            periodHasZoneService
                .put(this.periodHasZone.id, this.quantitiesToUpdate, type)
                .then(async () => {
                    this.loading = false;
                    this.quantitiesToUpdate = [];
                    this.$emit("updateIsEdit", false);
                    this.$emit("reload");
                    await this.loadPeriodHasComponents();
                    this.dialogSuccess("success");
                })
                .catch(() => {
                    this.loading = false;
                    this.dialogError("error");
                });
        },
        preventNav(event) {
            if (!this.isEditing) return;
            event.preventDefault();
            event.returnValue = "";
        },
        warningSaveQuantities(message) {
            if (!this.isEditing) {
                return new Promise(resolve => resolve(true));
            }
            return this.$swal({
                html: message,
                type: "warning",
                showCancelButton: true,
                confirmButtonColor: "red",
                confirmButtonText: this.$t("common.yes").toUpperCase(),
                cancelButtonText: this.$t("common.no").toUpperCase(),
                closeOnConfirm: true
            });
        },
        async disableTooltip(refName) {
            await this.$nextTick();
            if (
                $(this.$refs[refName])
                    .parent()
                    .width() >= $(this.$refs[refName]).width()
            ) {
                this.$root.$emit("bv::disable::tooltip", refName);
            }
        }
    },
    async created() {
        JustDigits();
        this.categoriesSelected = this.categories[0];

        if (this.periodHasZones && this.periodHasZones.length) {
            let currentIndexMonthPeriod = null;
            if (this.currentStamp) {
                currentIndexMonthPeriod = this.periodHasZones.findIndex(pz => pz.period.stampCode === this.currentStamp);
            } else {
                currentIndexMonthPeriod = this.periodHasZones.findIndex(pz => pz.period.stampCode === this.periodHasZone.period.stampCode);
            }
            if (currentIndexMonthPeriod > -1) {
                if (this.periodHasZones.length > 8) {
                    this.offsetControl = currentIndexMonthPeriod - 7;
                    if (this.offsetControl + 8 < this.periodHasZones.length) {
                        this.offsetControl++;
                    }
                }
                this.changePeriod(this.periodHasZones[currentIndexMonthPeriod], false);
            } else {
                this.changePeriod(this.periodHasZones[0], false);
            }
        }
        this.categoriesLevelSelected.push(this.categories);
        if (this.needModalRechallengeInfos) {
            this.openModalRechallengeInfos();
        }
        await this.loadPeriodHasComponents();
    },
    beforeMount() {
        window.addEventListener("beforeunload", this.preventNav);
        this.$once("hook:beforeDestroy", () => {
            window.removeEventListener("beforeunload", this.preventNav);
        });
    },
    watch: {
        reload: {
            immediate: true,
            handler(reload) {
                if (reload) {
                    this.created;
                }
            }
        }
    }
};
</script>
<style lang="scss" scoped>
.catalog-forecast-market-container {
    background-color: #fff;
    padding: 30px 50px;

    &-periods {
        > div {
            display: flex;
            justify-content: center;

            span {
                cursor: pointer;
            }

            span:not(.control) {
                position: relative;
                border: 1px solid #6f6f6f;
                border-radius: 3px;
                padding: 5px 10px;
                color: #6f6f6f;
                font-size: 22px;
                @media (max-width: 1440px) {
                    font-size: 18px;
                }

                &:hover,
                &.active {
                    color: #fff;
                    background-color: #6f6f6f;
                }

                &.open {
                    &:not(:hover):not(.active) {
                        color: #90bb8a;
                        border: 1px solid #90bb8a;
                    }

                    &:before {
                        content: "";
                        position: absolute;
                        bottom: -1px;
                        right: -1px;
                        border-style: solid;
                        border-width: 10px 10px 10px 10px;
                        border-color: transparent #00c24e #00c24e transparent;
                    }
                }
            }
        }
    }

    &-header {
        background-color: #f4f4f4;
        padding: 30px;
        .title {
            font-size: 32px;
        }
        .subtitle {
            font-size: 24px;
        }
        .rechallenged {
            span {
                text-transform: initial;
                color: #ffca39;
                font-weight: bold;
            }
        }
    }
    &-search {
        span {
            font-size: 16px;
            text-transform: initial;
        }
    }
    &-table {
        .count-reference {
            font-size: 18px;
            font-weight: bold;
            text-transform: capitalize;
        }
        .table-column {
            flex: 50%;
        }
        table {
            table-layout: auto;
            tr {
                th {
                    height: 135px;
                    background-color: #fff;
                    font-family: "oswald", Sans-Serif;
                    font-size: 20px;
                    text-align: center;
                    text-transform: uppercase;
                    vertical-align: bottom;
                    padding-bottom: 10px;
                    border-top: solid 1px transparent;
                    &.header {
                        vertical-align: top;
                        padding: 0 !important;
                        border-bottom: none !important;
                        > div {
                            height: 130px;
                        }
                        span {
                            padding: 5px;
                            font-family: Roboto;
                            width: 100%;
                            font-weight: normal;
                            color: #2d2d2d;
                        }
                        p {
                            display: flex;
                            flex: 1 1 auto;
                            align-items: center;
                            justify-content: center;
                            width: 100%;
                            margin: 10px 0;
                            font-size: 18px;
                            text-align: center;
                        }
                        &.firmorder {
                            min-width: 160px;
                            border: solid 1px #f86e6e;
                            span {
                                background-color: #f86e6e;
                            }
                        }
                        &.forecast {
                            min-width: 160px;
                            border: solid 1px #747795;
                            span {
                                color: #fff;
                                background-color: #747795;
                            }
                        }
                    }
                    &.sap {
                        width: 220px;
                        max-width: 250px;
                    }
                    &.ean {
                        width: 180px;
                        max-width: 200px;
                    }
                    &.name {
                        width: 400px;
                        max-width: 500px;
                    }
                    &.unit {
                        min-width: 150px;
                    }
                    &.info {
                        min-width: 80px;
                    }
                }
                td {
                    position: static;
                    height: 46px;
                    background-color: #fff;
                    padding: 10px 15px;
                    border-left: none;
                    border-right: none;
                    font-size: 16px;
                    border-left: solid 1px #343434;
                    border-right: solid 1px #343434;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                    overflow: hidden;
                    span {
                        font-size: 16px;
                    }
                    color: #767676;
                    &.sap {
                        width: 220px;
                        max-width: 250px;
                    }
                    &.ean {
                        width: 180px;
                        max-width: 200px;
                    }
                    &.name {
                        width: 400px;
                        max-width: 500px;
                        font-weight: bold;
                    }
                    &.unit {
                        min-width: 150px;
                    }
                    &.info {
                        text-align: center;
                        min-width: 80px;
                    }
                    &.firmorder,
                    &.forecast {
                        padding: 0;
                        min-width: 160px;
                        text-align: center;
                        border: solid 1px transparent;
                        input {
                            padding: 0;
                            text-align: center;
                            font-size: 16px;
                            background-color: transparent;
                            border: none;
                            width: 100%;
                            box-shadow: none;
                        }
                    }
                    &.firmorder {
                        border-left: solid 1px #f86e6e;
                        border-right: solid 1px #f86e6e;
                        &:not(.not-active):focus-within {
                            background-color: #f86e6e !important;
                            input {
                                color: #fff;
                            }
                        }
                        //&:focus-within {
                        //        border: solid 1px #F86E6E;
                        //    }
                        //    &:not(:focus-within) {
                        //        border: none;
                        //        border-left: solid 1px #F86E6E;
                        //        border-right: solid 1px #F86E6E;
                        //    }
                    }
                    &.forecast {
                        border-left: solid 1px #343434;
                        border-right: solid 1px #343434;
                        &:not(.not-active):focus-within {
                            background-color: #747795 !important;
                            input {
                                color: #fff;
                            }
                        }
                        //&:focus-within {
                        //        border: solid 1px #343434;
                        //    }
                        //    &:not(:focus-within) {
                        //        border: none;
                        //        border-left: solid 1px #343434;
                        //        border-right: solid 1px #343434;
                        //    }
                    }
                    &.not-active {
                        background-color: #6f6f6f !important;
                        color: #fff;
                        span,
                        input {
                            color: #fff;
                        }
                    }
                    &:last-child {
                        border-right: solid 1px #4d4d4d;
                    }
                }
                &:nth-child(odd) {
                    td {
                        background-color: #ebe7e7;
                    }
                }
                &.notfound {
                    height: 150px;
                    td {
                        text-align: center;
                        border: #343434 1px solid;
                        background-color: #fff;
                    }
                }
                &.not-active {
                    td {
                        background-color: #6f6f6f;
                        color: #fff;
                        span,
                        input {
                            color: #fff;
                        }
                    }
                }
                &:last-child {
                    border-bottom: solid 1px #4d4d4d;
                }
            }
        }
    }

    div.disable-arrow {
        color: lightgray;
        cursor: default;

        span {
            cursor: default;
        }
    }
}
</style>



// WEBPACK FOOTER //
// CatalogForecastMarket.vue?3365f66b